import { useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, Order, PaymentMethod } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatTime } from "../../lib/time";
import { Dialog } from "../../ui/Dialog";
import { correctablePlatform, methodsFor } from "./order-view";

interface Props {
  order: Order;
  /** The owner's code, for an order of Takeaway or Uber Eats (Antalya, 17/10/2026). */
  authorization?: Authorization | undefined;
  onDone: (order: Order) => void;
  onClose: () => void;
}

/**
 * Another payment method afterwards (e.g. the customer paid with Bancontact after all), without a code
 * and in either direction (Furkan, 07/10/2026). The server keeps the original payment and records the
 * change with the name of this till. Split payments are changed one by one.
 *
 * An order of Takeaway or Uber Eats with the owner's code (Antalya, 17/10/2026), with "Online" among the
 * choices: the server then sets Takeaway's fee and the note right and writes it in the shop's log.
 */
export function PaymentMethodDialog({ order, authorization, onDone, onClose }: Props) {
  const { api, toast } = useTill();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const payments = order.payments.filter((payment) => payment.kind === "payment");

  const change = async (paymentId: string, from: PaymentMethod, to: PaymentMethod) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.request<Order>("POST", `/orders/${order.id}/payments/${paymentId}/method`, {
        body: { method: to },
        ...(authorization && { staffToken: authorization.token }),
      });
      onDone(updated);
      toast(t.orders.methodChanged(t.paymentMethods[from], t.paymentMethods[to]));
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
      setBusy(false);
    }
  };

  return (
    <Dialog title={t.orders.methodTitle} onClose={onClose}>
      <p>
        {t.orders.code} <span className="code">{order.code}</span> · {formatEuro(order.totalCents)}
      </p>
      {payments.length === 0 && <p>{t.orders.noPayments}</p>}
      {payments.map((payment) => (
        <div className="grp" key={payment.id}>
          {payments.length > 1 && (
            <h3>
              {formatTime(payment.createdAt)} · {formatEuro(payment.amountCents)}
            </h3>
          )}
          <div className="chips">
            {methodsFor(order).map((method) => (
              <button
                key={method}
                type="button"
                className="chip"
                aria-pressed={method === payment.method}
                disabled={busy || method === payment.method}
                onClick={() => void change(payment.id, payment.method, method)}
              >
                {t.paymentMethods[method]}
              </button>
            ))}
          </div>
        </div>
      ))}
      <p className="note">{correctablePlatform(order) ? t.orders.methodHintPlatform : t.orders.methodHint}</p>
      {error !== null && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}
